<script setup lang="ts">
import GuardianAddEditFormComponent from "@/components/models/patient/forms/GuardianAddEditFormComponent.vue";
import { useForm } from "vee-validate";
import { definePage } from "unplugin-vue-router/runtime";
import { useI18n } from "vue-i18n";
import { usePatient, useUpdatePatient } from "@/composables/models/patient/usePatient";
import { useRoute, useRouter } from "vue-router";
import { computed, type Ref } from "vue";
import { type TPatient, type TGuardian } from "@/api/models/patient/patient";
import { useSnackStore } from "@/stores/snackStore";
import { object, boolean } from "yup";
import { createGuardianValidationSchema } from "@/validationSchemas/models/patientValidationSchema";
import { useAbility } from "@casl/vue";
import FormActions from "@/components/shared/form/FormActions.vue";
import { useFormSubmitFlowLock } from "@/composables/shared/useFormSubmitFlowLock";
definePage({
  meta: {
    title: "generic.action.addEntity",
    titleParams: { entity: "generic.entity.guardian" },
    checkPrivileges: () => {
      const { can } = useAbility();
      return can($permActions.UPDATE, $permSubjects.PATIENT__PATIENT);
    },
  },
});

const { t } = useI18n();

const route = useRoute();
const parentPatientId = computed(() => (route.params as { id: string }).id);
const redirectUrl = computed(() => route.query.redirectUrl as string | undefined);
const router = useRouter();
const snackStore = useSnackStore();
const { data: parentPatient, isLoading } = usePatient({ id: parentPatientId }) as unknown as {
  data: Ref<(TPatient & { guardians?: TGuardian[] }) | undefined>;
  isLoading: boolean;
};
const { mutateAsync: updatePatient, isPending: isUpdatePending } = useUpdatePatient();
const { runSubmitFlow, isSubmitFlowPending } = useFormSubmitFlowLock();

const parentPatientContacts = computed(() => ({
  emails: parentPatient.value?.emails || [],
  phones: parentPatient.value?.phones || [],
  addresses: parentPatient.value?.addresses || [],
}));

const validationSchema = computed(() =>
  object({
    guardian_data: object(createGuardianValidationSchema(t)).nullable(),
    is_payer: boolean().nullable(),
  })
);

const { handleSubmit } = useForm({
  validationSchema,
  initialValues: {
    is_payer: true,
  },
});

const submit = handleSubmit((result) => {
  void runSubmitFlow(async () => {
    const mergedGuardians = [
      ...(parentPatient.value?.guardians ?? []).map((g) => ({
        ...g,
        guardian_data: undefined,
        is_payer: result.is_payer ? false : g.is_payer,
      })),
      result,
    ];
    await updatePatient(
      {
        id: parentPatientId.value,
        guardians: mergedGuardians,
      },
      {
        onSuccess: async () => {
          snackStore.showSuccess(
            t("generic.messages.success.add", {
              entity: t("generic.entity.guardian"),
            })
          );
          if (redirectUrl.value) {
            await router.push(redirectUrl.value);
          } else {
            await router.push(`/rooms/patient/${parentPatientId.value}`);
          }
        },
      }
    );
  });
});
</script>

<template>
  <loading v-if="isLoading" />
  <template v-else>
    <v-form
      v-if="parentPatient"
      class="tw:w-full"
      :validation-schema="validationSchema"
      @submit.prevent="submit"
      data-testid="guardian-add-form"
    >
      <guardian-add-edit-form-component
        v-if="parentPatient"
        :parent-patient-contacts="parentPatientContacts"
        :validation-schema="validationSchema"
        :disable-is-payer="!parentPatient?.guardians?.length"
      />
      <form-actions
        :loading="isSubmitFlowPending || isUpdatePending"
        :on-save="submit"
        :on-cancel="() => (redirectUrl ? router.push(redirectUrl) : router.back())"
        save-icon="mdi-account-plus-outline"
      >
      </form-actions>
    </v-form>
  </template>
</template>
